import SendIcon from "@mui/icons-material/Send"
import { Stack, Collapse, AlertTitle } from "@mui/material"
import dayjs from "dayjs"
import { KeyboardEventHandler, ReactElement } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"

import AppLoadingButton from "../../components/buttons/appLoadingButton"
import AppAlert from "../../components/dataDisplay/alerts/appAlert"
import ValidatedAutocomplete from "../../components/inputs/validated/validatedSelect"
import ValidatedTextField from "../../components/inputs/validated/validatedTextField"
import { LCT_ASSESSOR_STATUS_MAPPINGS } from "../../components/popUps/assessDevice/assessDeviceForm"
import { Datasets } from "../../constants/enums"
import {
    WORST_CASE_POWER_LEVEL,
    APPLICABLE_STANDARD,
    SSC_MIN_3_PHASE,
    CHARGER_SYSTEM_RATED_LE_75,
    CHARGER_SYSTEM_RATED_GE_75,
    INFORMATION_PROVIDED,
    IN_ACCORDANCE_WITH_EN_61000_3_11,
    ZMAX,
    COMPLIANCE_STATUS,
    mapRegisterNameToEVDCValues,
    EV_DC_APPLICABLE_STANDARD_STATUSES,
    EV_DC_VOLTAGE_IN_ACCORDANCE,
    EV_DC_VOLTAGE_FLUCTUATION_AND_FLICKER_STATUSES,
} from "../../constants/lct/evdc/assessDeviceForm"
import {
    POSITIVE_INTEGER_REGEX,
    POSITIVE_NUMBER_REGEX,
} from "../../constants/patterns"
import { SINGLE_DEMAND_DEVICE } from "../../router/routes"
import useAssessDevice from "../../services/middleware/hooks/deviceRegisters/useAssessDevice"
import { IFullEVDCDevice } from "../../services/middleware/hooks/deviceRegisters/useGetFullDevice"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

const invalidNumberMessage = "Value must be a positive number"

interface IAssessEVDCDemandFormProps {
    data: IFullEVDCDevice
    isLoading: boolean
    mutate: () => void
}

const getSingleDeviceUrl = (deviceRef: string): string =>
    SINGLE_DEMAND_DEVICE.replace(":dataset", "EVDC").replace(
        ":deviceId",
        encodeURIComponent(deviceRef)
    )

/** Form component for submitting assessment for a demand device.
 */
const AssessEVDCDemandForm = ({
    data,
    isLoading,
    mutate,
}: IAssessEVDCDemandFormProps): ReactElement => {
    const navigate = useNavigate()

    const { control, handleSubmit, watch } = useForm({
        defaultValues: mapRegisterNameToEVDCValues(data.validation),
        shouldUnregister: true,
        mode: "all",
    })

    const { error, isSending, postLctResponse } = useAssessDevice(() => {
        mutate()
        navigate(getSingleDeviceUrl(data.header["Device Ref"]))
    })

    const submitResponse = (): void => {
        const updatedValues = {
            ComplianceStatus: watch(COMPLIANCE_STATUS) as string,
            WorstCasePowerLevel: watch(WORST_CASE_POWER_LEVEL) as number,
            ApplicableStandard: watch(APPLICABLE_STANDARD) as string,
            SscMin3Phase:
                watch(SSC_MIN_3_PHASE) === ""
                    ? null
                    : (watch(SSC_MIN_3_PHASE) as number),
            ChargerSystemRatedLe75: watch(CHARGER_SYSTEM_RATED_LE_75) as number,
            ChargerSystemRatedGe75: watch(CHARGER_SYSTEM_RATED_GE_75) as number,
            InformationProvided: watch(INFORMATION_PROVIDED) as string,
            InAccordanceWithEn61000311: watch(
                IN_ACCORDANCE_WITH_EN_61000_3_11
            ) as number,
            Zmax: watch(ZMAX) === "" ? null : (watch(ZMAX) as number),
        }

        postLctResponse(
            data.header["Device Ref"],
            dayjs(data.header["Published At"]),
            Datasets.EVDC,
            updatedValues
        )
    }

    const handleKeyPress: KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (event.key === "Enter" && !event.altKey) {
            event.preventDefault()
        }
    }

    return (
        <Stack spacing={GRID_SPACING * 2}>
            <ValidatedTextField
                control={control}
                required
                registerName={WORST_CASE_POWER_LEVEL}
                placeholder="Worst Case Power Level %"
                label="Worst Case Power Level %"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_INTEGER_REGEX,
                        message: "Power level must be a positive integer",
                    },
                }}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={APPLICABLE_STANDARD}
                options={Object.keys(EV_DC_APPLICABLE_STANDARD_STATUSES)}
                label="Applicable Harmonic Emission Standard"
                getOptionLabel={(option) =>
                    EV_DC_APPLICABLE_STANDARD_STATUSES[
                        option as keyof typeof EV_DC_APPLICABLE_STANDARD_STATUSES
                    ]
                }
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={SSC_MIN_3_PHASE}
                placeholder="SSC Minimum 3 Phase"
                label="SSC Minimum 3 Phase"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={CHARGER_SYSTEM_RATED_LE_75}
                placeholder="Charger System Rated Less Than or Equal to 75A"
                label="Charger System Rated Less Than or Equal to 75A"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={CHARGER_SYSTEM_RATED_GE_75}
                placeholder="Charger System Rated Greater Than or Equal to 75A"
                label="Charger System Rated Less Greater or Equal to 75A"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={INFORMATION_PROVIDED}
                label="Information Provided on Voltage Fluctuation and Flicker"
                options={Object.keys(
                    EV_DC_VOLTAGE_FLUCTUATION_AND_FLICKER_STATUSES
                )}
                getOptionLabel={(option) =>
                    EV_DC_VOLTAGE_FLUCTUATION_AND_FLICKER_STATUSES[
                        option as keyof typeof EV_DC_VOLTAGE_FLUCTUATION_AND_FLICKER_STATUSES
                    ]
                }
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={IN_ACCORDANCE_WITH_EN_61000_3_11}
                label="In accordance with EN 61000-3-11"
                options={Object.keys(EV_DC_VOLTAGE_IN_ACCORDANCE)}
                getOptionLabel={(option) =>
                    EV_DC_VOLTAGE_IN_ACCORDANCE[
                        option as keyof typeof EV_DC_VOLTAGE_IN_ACCORDANCE
                    ]
                }
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={ZMAX}
                placeholder="Maximum Impedance for Voltage Fluctuation and Flicker"
                label="Maximum Impedance for Voltage Fluctuation and Flicker"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={COMPLIANCE_STATUS}
                label="Set compliance status"
                options={Object.keys(LCT_ASSESSOR_STATUS_MAPPINGS)}
                getOptionLabel={(option) =>
                    LCT_ASSESSOR_STATUS_MAPPINGS[
                        option as keyof typeof LCT_ASSESSOR_STATUS_MAPPINGS
                    ]
                }
                disablePortal={false}
            />
            <Collapse
                style={{ marginBottom: "16px" }}
                in={!!error}
                mountOnEnter
            >
                <AppAlert severity={error?.level}>
                    <AlertTitle>{error?.message}</AlertTitle>
                    {error?.displayDetail}
                </AppAlert>
            </Collapse>
            <AppLoadingButton
                style={{
                    marginLeft: "auto",
                    marginTop: "32px",
                }}
                onClick={handleSubmit(submitResponse)}
                disabled={isLoading || isSending}
                variant="contained"
                loading={isSending}
                endIcon={<SendIcon />}
            >
                Submit Assessment
            </AppLoadingButton>
        </Stack>
    )
}

export default AssessEVDCDemandForm
